import clsx from "clsx";
import Link from "next/link";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { LoadingSkeleton } from "shared-components";

import { BASE_SEARCH_PRODUCTS_URL } from "@/constants/pages";
import { useThemes } from "@/hooks/use-themes";
import type { CommonTheme } from "@/stores/use-themes-store";

import { useMainThemeTopics } from "../Sections/MainThemesLayout";
import { HEADER_SECTIONS, type SectionKey } from "../Sections";

import type { JSX, ReactNode } from "react";
import type { MobileTopicLink } from "./types";

interface AccordionControl {
  /** Id do tópico aberto no momento (estado vive no `Header`). */
  expandedTopicId: string | null;
  onToggleTopic: (topicId: string) => void;
  /** Chamado ao clicar em qualquer link, para fechar o menu. */
  onNavigate: () => void;
}

const subLinkClassName = "block py-[0.4rem] text-sm text-brand-primary";

/** Lista de subtópicos com a linha vertical à esquerda. */
const TopicLinks = ({
  links,
  onNavigate,
}: {
  links: MobileTopicLink[];
  onNavigate: () => void;
}): JSX.Element => (
  <div className="mb-1 ml-1 border-l border-base-border pl-2">
    {links.map((link) => (
      <Link
        key={link.name}
        href={link.url}
        onClick={onNavigate}
        className={clsx(subLinkClassName, link.name.endsWith("→") && "font-bold text-brand-accent")}
      >
        {link.name}
      </Link>
    ))}
  </div>
);

/** Linha de tópico: título + seta; os filhos aparecem quando `isExpanded`. */
const TopicRow = ({
  name,
  isExpanded,
  onToggle,
  children,
}: {
  name: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}): JSX.Element => (
  <div>
    <button
      type="button"
      aria-expanded={isExpanded}
      onClick={onToggle}
      className="flex w-full items-center justify-between py-[0.6rem] text-start font-bold"
    >
      <span>{name}</span>
      {isExpanded ? <MdKeyboardArrowUp size="1.25rem" /> : <MdKeyboardArrowDown size="1.25rem" />}
    </button>

    {isExpanded && children}
  </div>
);

const TopicsSkeleton = (): JSX.Element => (
  <div className="flex flex-col gap-2">
    {Array.from({ length: 4 }, (_, index) => (
      <LoadingSkeleton key={index} kind="paragraph" className="max-w-[14rem]" />
    ))}
  </div>
);

/** Masculino / Feminino / Infantil: tópicos vindos de `useMainThemeTopics`. */
const MainThemeTopics = ({
  theme,
  expandedTopicId,
  onToggleTopic,
  onNavigate,
}: AccordionControl & { theme: string }): JSX.Element => {
  const topics = useMainThemeTopics(theme);

  return (
    <>
      {topics.map((topic) => (
        <TopicRow
          key={topic.id}
          name={topic.name}
          isExpanded={expandedTopicId === topic.id}
          onToggle={() => onToggleTopic(topic.id)}
        >
          <TopicLinks links={topic.links} onNavigate={onNavigate} />
        </TopicRow>
      ))}
    </>
  );
};

/** Um tema raiz; só busca os sub-temas quando está expandido. */
const ThemeTopic = ({
  theme,
  isExpanded,
  onToggle,
  onNavigate,
}: {
  theme: CommonTheme;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}): JSX.Element => {
  const { subThemes, isLoadingSubThemes } = useThemes({ themeId: isExpanded ? theme.id : undefined });

  return (
    <TopicRow name={theme.name} isExpanded={isExpanded} onToggle={onToggle}>
      {isLoadingSubThemes && subThemes.length === 0 ? (
        <div className="ml-1 border-l border-base-border pl-2">
          <TopicsSkeleton />
        </div>
      ) : (
        <TopicLinks
          onNavigate={onNavigate}
          links={[
            ...subThemes.map((subTheme) => ({
              name: subTheme.name,
              url: `${BASE_SEARCH_PRODUCTS_URL}?themes=${subTheme.id}`,
            })),
            { name: "Ver tudo →", url: `${BASE_SEARCH_PRODUCTS_URL}?themes=${theme.id}` },
          ]}
        />
      )}
    </TopicRow>
  );
};

const ThemesTopics = ({ expandedTopicId, onToggleTopic, onNavigate }: AccordionControl): JSX.Element => {
  const { commonThemes, isLoadingRootThemes } = useThemes();

  if (isLoadingRootThemes) {
    return <TopicsSkeleton />;
  }

  return (
    <>
      {commonThemes.map((theme) => (
        <ThemeTopic
          key={theme.id}
          theme={theme}
          isExpanded={expandedTopicId === theme.id}
          onToggle={() => onToggleTopic(theme.id)}
          onNavigate={onNavigate}
        />
      ))}
    </>
  );
};

/** Licenciados: lista simples (não há subníveis). */
const LicensedTopics = ({ onNavigate }: Pick<AccordionControl, "onNavigate">): JSX.Element => {
  const { licensedThemes, isLoadingLicensedThemes } = useThemes({ shouldFetchLicensed: true });

  if (isLoadingLicensedThemes) {
    return <TopicsSkeleton />;
  }

  return (
    <TopicLinks
      onNavigate={onNavigate}
      links={licensedThemes.map((theme) => ({
        name: theme.name,
        url: `${BASE_SEARCH_PRODUCTS_URL}?themes=${theme.id}`,
      }))}
    />
  );
};

/** URL do "Ver tudo" do título de cada seção. */
const getSectionSeeAllUrl = (sectionKey: SectionKey, licensedRootThemeId?: string): string => {
  switch (sectionKey) {
    case "masculino":
    case "feminino":
    case "infantil":
      return `${BASE_SEARCH_PRODUCTS_URL}?public=${sectionKey.toUpperCase()}`;
    case "licenciados":
      return licensedRootThemeId
        ? `${BASE_SEARCH_PRODUCTS_URL}?themes=${licensedRootThemeId}`
        : BASE_SEARCH_PRODUCTS_URL;
    default:
      return BASE_SEARCH_PRODUCTS_URL;
  }
};

interface Props extends AccordionControl {
  sectionKey: SectionKey;
}

/**
 * Conteúdo da seção aberta no menu mobile: título + "Ver tudo" e a lista de tópicos expansíveis.
 */
export const MobileSectionContent = ({ sectionKey, ...accordion }: Props): JSX.Element => {
  const section = HEADER_SECTIONS.find(({ key }) => key === sectionKey);
  const { licensedRootTheme } = useThemes();

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h5 className="font-bold">{section?.name}</h5>

        <Link
          href={getSectionSeeAllUrl(sectionKey, licensedRootTheme?.id)}
          onClick={accordion.onNavigate}
          className="text-sm font-bold text-brand-accent"
        >
          Ver tudo
        </Link>
      </div>

      {(sectionKey === "masculino" || sectionKey === "feminino" || sectionKey === "infantil") && (
        <MainThemeTopics theme={sectionKey} {...accordion} />
      )}
      {sectionKey === "temas" && <ThemesTopics {...accordion} />}
      {sectionKey === "licenciados" && <LicensedTopics onNavigate={accordion.onNavigate} />}
    </div>
  );
};
